import store from '@/store';
import poiElements from '@data/poiElements';
import {
    formatTodayOpeningHours,
    formatOpeningHours,
    formatDate,
    formatTime,
    formatHeight,
    formatOpeningSeason,
    formatTimeAgo,
    formatDuration
} from '@/utils/format';
import {
    getPaymentOptionLabel,
    getVehicleLabel,
    getServiceLabel,
    getOperationModeLabel,
    getPoiTypeLabel,
    getAuthOptionLabel,
} from '@/utils/poi-detail-labels';
import { getHtmlDescription } from './poi-data/content-descriptions';
import { getPriceList, getMainPrice, getPrice, getPriceUnit, getHourlyPrice } from './poi-data/prices';
import { isResultMultiPoi } from '@/utils/poi-data/is-multi-poi';
import { isShorttermParking } from '@/utils/poi-data/is-shortterm-parking';
import { getPoiConfig } from '@/utils/poi-data/poi-config';
import { getOpeningTime, getOpeningTimeForSeason } from '@/utils/poi-data/openings';

export const PRICE_SORTING_MODES = ['gasStations'];

export const ONLY_LOGGED_IN_AVAILABLE = ['sights', 'travelRadar'];

export function getImageForCategory(type, category, imageType = 'image', result = false) {
    const parentApi = getParentApi(type)
    if(parentApi) {
        type = parentApi
    }
    const config = getPoiConfig(type);
    if(type === 'gasStations') {
        if(result?.data?.header?.logos?.length) {
            const logoObj = result.data.header.logos[0]
            return logoObj.url
        } else {
            if(config.imageName) {
                return config[imageType];
            }
            return false
        }
    }
    if (!config || !config.categories){
        // some poiTypes have one global image to display
        if(config.imageName) {
            return config[imageType];
        }
        return false;
    }
    let foundCategory = config.categories.find(
        (categoryItem) => categoryItem.value === category,
    );
    if (!foundCategory) {
        const parentCategories = config.categories.filter(
            (categoryItem) => categoryItem.children,
        );
        if (parentCategories.length) {
            parentCategories.forEach((parentCategory) => {
                const childCategory = parentCategory.children.find(
                    (child) => child.value === category,
                );
                if (childCategory) {
                    foundCategory = childCategory;
                    return true;
                }
            });
        }
    }

    if (!foundCategory || !foundCategory[imageType]) return false;
    return foundCategory[imageType];
}

export function getActivePoi() {
    const poiResults = store.getters.poiResults;
    const activePoiId = store.getters.activePoiId;
    for (const poiType in poiResults) {
        let active = poiResults[poiType].find((poi) => {
            const id = getPoiId(poiType, poi);
            return id === activePoiId;
        });
        if (active) {
            active.poiType = poiType;
            active.label = getPoiTypeLabel(poiType)

            return active;
        }
    }
    return false;
}

export function getPOIResultByType(response, type) {
    switch (type) {
        case 'traffic':
        case 'prediction':
            return response.data.results ? response.data.results : response.data.data;
        case 'favourites':
            return response.data.results || response.data.favorites;
        case 'nearbyStations': 
            return convertNearbyStationsResults(response.data);
        case 'travelRadar':
            return response.data.data
        default:
            return response.data.results;
    }
}

export function getPoiId(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    if( multiPoi ) {
        if(poiType === 'favourites' && poiResult.type !== 'ADDRESS' && poiResult.typeSpecificAttributes.favorite && poiResult.typeSpecificAttributes.favorite.referenceId && poiResult.typeSpecificAttributes.favorite.referenceId !== '') return poiResult.typeSpecificAttributes.favorite.referenceId
        return poiResult.id;
    } else if (poiType === 'traffic' || poiType === 'camera' || poiType === 'prediction') {
        if(poiResult.data) return poiResult.data.header ? poiResult.data.header.id : false
        return poiResult.header ? poiResult.header.id : poiResult.id;
    } else if (poiType === 'nearbyStations') {
        return poiResult.externalId || poiResult.inputParameters.externalId; // TODO: check for favourite response -> station-monitor has different value
    } else if(poiType === 'favourites') {
        return poiResult.id
    } else if(poiType === 'travelRadar') {
        return poiResult.header ? poiResult.header.id : poiResult.id
    } else {
        return poiResult.data.header.id;
    }
}

export function getPoiType(poiType, poiResult) {
    let type = poiType
    if(poiType === 'favourites') {
        if(poiResult.type === 'ADDRESS') return false
        const typeConfig = getFavouriteTypeConfig(poiResult.type)
        type = typeConfig.value
        if(type && isPartner(type.toLowerCase())) type = 'partners'
        if(type && type === 'CAMERA') type = 'traffic'
    }
    return type
}

export function getBasicPreparedPoiData(poiType, poiResult) {
    const multiPoi = isResultMultiPoi(poiResult);
    const preparedData = {
        type: getPoiType(poiType, poiResult),
        subType: getSubType(poiType, poiResult),
        title: getTitle(poiType, poiResult, multiPoi),
        address: getAddress(poiType, poiResult, multiPoi),
        image: getImageForPoiCategory(poiType, poiResult, multiPoi),
        partnerTag: getPartnerTag(poiType, poiResult, multiPoi),
        price: getPrice(poiType, poiResult, multiPoi),
        distance: getDistance(poiType, poiResult, multiPoi),
        roadName: getRoadName(poiType, poiResult),
        country: getCountry(poiType, poiResult),
        id: getPoiId(poiType, poiResult, multiPoi),
        maxDuration: getMaxDuration(poiType, poiResult),
        hourlyPrice: getHourlyPrice(poiType, poiResult),
        paymentOptions: getPaymentOptions(poiType, poiResult),
        isFavouritePoi: poiType === 'favourites',
        longDescription: getLongDescription(poiType, poiResult, multiPoi),
        updatedAgo: getUpdatedAgo(poiType, poiResult),
        lastUpdated: getLastUpdated(poiType, poiResult, multiPoi),
        plugTypes: getPlugTypes(poiType, poiResult),
        authentication: getAuthentication(poiType, poiResult)
    };

    // Opening time
    const OPENING_TIME_TYPES = ['oeamtcLocations', 'partners', 'gasStations', 'roadHouses', 'favourites', 'sights', 'chargingStations'];
    const SEASON_TIME_TYPES = ['camping'];
    if (OPENING_TIME_TYPES.includes(poiType) || isPartner(poiType)) {
        preparedData.openingTime = getOpeningTime(poiResult);
    } else if (SEASON_TIME_TYPES.includes(poiType)) {
        preparedData.openingTime = getOpeningTimeForSeason(poiResult);
    }

    // Fee availability
    const FEE_TYPES = ['carParks', 'shorttermparking', 'favourites'];
    if (FEE_TYPES.includes(poiType)) {
        preparedData.feeAvailability = getFeeAvailability(poiType, poiResult);
    }

    // availability
    const AVAILABILITY_TYPES = ['carParks', 'shorttermparking', 'favourites'];
    if (AVAILABILITY_TYPES.includes(poiType)) {
        preparedData.availability = getAvailability(poiResult, poiType, multiPoi);
    }

    // Price unit
    if (preparedData.price) {
        preparedData.priceUnit = getPriceUnit(poiType);
    }

    // category / type name in the list
    const TYPE_NAME_TYPES = ['carParks', 'shorttermparking', 'favourites',];
    if (TYPE_NAME_TYPES.includes(poiType)) {
        if(isShorttermParking(poiType, poiResult)) {
            preparedData.typeName = getCategoryLabelByResult(poiType, poiResult);
        }
    }

    // category name for poi detail
    const CATEGORY_NAME_TYPES = ['oeamtcLocations', 'carParks', 'partners', 'favourites', 'sights', 'travelRadar', 'camping', 'traffic'];
    if (CATEGORY_NAME_TYPES.includes(poiType) || isPartner(poiType)) {
        preparedData.categoryName = getCategoryLabelByResult(poiType, poiResult);
        if(poiType === 'sights') { // only show it in the list for certain POI types
            preparedData.showCategoryNameInList = true
        }
        if(poiType === 'traffic') { // only show this for cameras
            if(poiResult.type === 'CAMERA' || poiResult.subType === 'camera') {
                preparedData.categoryName = getCategoryLabelByResult(poiType, poiResult);
            } else {
                preparedData.categoryName = false
            }
        }
    } else { // for all others, use the detail name
        preparedData.categoryName = getDetailNameByType(poiType)
    }

    //tracking info for parking poi items
    const PARKING_NAME_TYPES = ['carParks', 'shorttermparking'];
    if (PARKING_NAME_TYPES.includes(poiType)) {
        preparedData.trackingCategory = 'Parking';
        preparedData.trackingAction = 'Select';
        preparedData.ga4EventName = 'parking';
        preparedData.ga4Parameter1Name = 'select_poi';
        if(isShorttermParking(poiType, poiResult)) {
            preparedData.trackingLabel = 'Kurzparkzone';
            preparedData.ga4Parameter1Value = 'kurzparkzone';
        } else {
            preparedData.trackingLabel = 'Standard Parking';
            preparedData.ga4Parameter1Value = 'kurzparkzone';
        }
    }

    //tracking info for other poi items
    const TRACKING_POI_TYPES = ['gasStations', 'partners', 'greenzone', 'roadHouses', 'nearbyStations', 'camping', 'passRoads', 'traffic', 'oeamtcLocations', 'tollRoads', 'tunnels', 'sights', 'travelRadar', 'chargingStations'];
    if(TRACKING_POI_TYPES.includes(poiType)) {
        preparedData.trackingCategory = getPoiConfig(poiType).trackingCategory;
        preparedData.trackingAction = getPoiConfig(poiType).trackingAction;
        preparedData.ga4EventName = getPoiConfig(poiType).trackingCategory.replace(/ /g, "_").toLowerCase();
        preparedData.ga4Parameter1Name = 'select_poi';
        if(poiType === 'gasStations') {
            preparedData.ga4EventName = 'gas_station';
            preparedData.trackingLabel = preparedData.title;
            preparedData.ga4Parameter1Value = preparedData.title.replace(/ /g, "_").toLowerCase();
        } else if(poiType === 'partners') {
            let categories = getPoiConfig(poiType).categories.filter(el => el.labelAlt === preparedData.categoryName)[0];
            preparedData.ga4EventName = 'partner';
            preparedData.trackingLabel = categories.trackingLabel;
            preparedData.ga4Parameter1Value = categories.trackingLabel.replace('&', "and").replace(/ /g, "_").toLowerCase();
        } else if(poiType === 'traffic') {
            preparedData.trackingLabel = 'Current Traffic';
            preparedData.ga4Parameter1Value = 'current_traffic';
        } else if(poiType === 'oeamtcLocations') {
            preparedData.trackingLabel = 'Offices';
            preparedData.ga4EventName = 'oeamtc_office';
            preparedData.ga4Parameter1Value = 'oeamtc_office';
        } else if(poiType === 'sights') {
            getPoiConfig(poiType).categories.forEach(el => {
                if(el.label.indexOf('ö')) el.labelAlt = el.labelAlt.replace(/ö/g, 'o')
                if(el.label.indexOf('ä')) el.labelAlt = el.labelAlt.replace(/ä/g, 'a')
                if(el.labelAlt === preparedData.categoryName) {
                    preparedData.trackingLabel = el.trackingLabel;
                    preparedData.ga4Parameter1Value = el.trackingLabel.toLowerCase();
                }
            })
        } else if(poiType === 'travelRadar') {
            preparedData.trackingLabel = 'Warning';
            preparedData.ga4Parameter1Value = 'travel_warning';
        } else if(poiType === 'nearbyStations') {
            preparedData.trackingLabel= getPoiConfig(poiType).trackingLabel;
            preparedData.ga4Parameter1Value = 'station';
            preparedData.ga4EventName = 'station';
        } else {
            preparedData.trackingLabel= getPoiConfig(poiType).trackingLabel;
            preparedData.ga4Parameter1Value = getPoiConfig(poiType).trackingLabel.replace('&', "and").replace(/ /g, "_").toLowerCase();
        }
    }

    // Centered List Row
    const CENTERED_LIST_ROW_TYPES = ['nearbyStations'];
    if (CENTERED_LIST_ROW_TYPES.includes(poiType)) {
        preparedData.centeredListRow = true;
    }

    // connected pois
    const CONNECTED_POI_TYPES = ['roadHouses']
    if(CONNECTED_POI_TYPES.includes(poiType)) {
        preparedData.connectedPois = getConnectedPois(poiResult)
    }

    const COORDINATES_TYPES = ['camping'];
    if (COORDINATES_TYPES.includes(poiType)) {
        preparedData.coordinates = getCoordinates(poiResult);
    }

    return preparedData;
}

export function getAdvancedPreparedPoiData(poiType, poiResult) {
    const multiPoi = isResultMultiPoi(poiResult);
    if(multiPoi) {
        return {}
    }
    const preparedData = {
        zip: getZip(poiType, poiResult, multiPoi),
        city: getCity(poiType, poiResult, multiPoi),
        category: getCategory(poiType, poiResult, multiPoi),
        bigImages: getBigImages(poiType, poiResult, multiPoi),
        todayMaxOpeningHours: getTodayMaxOpeningHours(poiType, poiResult, multiPoi),
        openingSections: getOpeningSections(poiType, poiResult, multiPoi),
        email: getEmail(poiType, poiResult, multiPoi),
        phone: getPhone(poiType, poiResult, multiPoi),
        website: getWebsite(poiType, poiResult, multiPoi),
        texts: getTexts(poiType, poiResult, multiPoi),
        additionalInformation: getAdditionalInformation(poiType, poiResult, multiPoi),
        lastUpdated: getLastUpdated(poiType, poiResult, multiPoi),
        validity: getValidity(poiType, poiResult, multiPoi),
        source: getSource(poiType, poiResult, multiPoi),
        nearbyPOIs: getNearbyPOIs(poiType, poiResult),
        priceList: getPriceList(poiType, poiResult),
        mainPrice: getMainPrice(poiType, poiResult),
        freeMinutes: getFreeMinutes(poiType, poiResult),
        maximumHeight: getMaximumHeight(poiType, poiResult),
        accessibility: getAccsessibility(poiType, poiResult),
        partnerDescription: getPartnerDescription(poiType, poiResult),
        services: getServices(poiType, poiResult),
        operationMode: getOperationMode(poiType, poiResult),
        operator: getOperator(poiType, poiResult),
        videoPicture: getVideoPicture(poiType, poiResult),
        pdf: getPdf(poiType, poiResult),
        additionalLink: getAdditionalLink(poiType, poiResult),
        roadInformation: getRoadInformation(poiType, poiResult),
        roadFromTo: getRoadFromTo(poiType, poiResult),
        trafficMessages: getTrafficMessages(poiType, poiResult),
        introText: getIntroText(poiType, poiResult),
        company: getCompany(poiType, poiResult),
        isPartner: isPartner(poiType) || poiType === 'partners', // for info order on poi detail page
        detailName: getDetailName(poiType, poiResult),
        comments: getComments(poiType, poiResult),
    };

    const OPENING_SEASONS_TYPES = ['camping'];
    if (OPENING_SEASONS_TYPES.includes(poiType)) {
        preparedData.openingSeasons = getOpeningSeasons(poiResult);
    }

    const OECC_TYPES = ['camping'];
    if (OECC_TYPES.includes(poiType)) {
        preparedData.oeccPartnerDescription = getOeccPartnerDescription(poiType, poiResult);
    }

    const PUBLIC_TRANSPORT_CONNECTION_TYPES = ['carParks', 'sights'];
    if (PUBLIC_TRANSPORT_CONNECTION_TYPES.includes(poiType)) {
        preparedData.publicTransportConnection = true;
        if(isShorttermParking(poiType, poiResult)) {
            preparedData.publicTransportConnection = false;
        }
    }

    const PARKING_SPACES_TYPES = ['carParks', 'roadHouses'];
    if (PARKING_SPACES_TYPES.includes(poiType) || poiResult.isChild) {
        preparedData.parkingSpaces = getParkingSpaces(poiType, poiResult);
    }

    const STATION_ID_TYPES = ['nearbyStations'];
    if (STATION_ID_TYPES.includes(poiType)) {
        preparedData.stationId = getStationId(poiResult);
    }

    const LINES_TYPES = ['nearbyStations'];
    if (LINES_TYPES.includes(poiType)) {
        preparedData.lines = getLines(poiResult);
    }

    return preparedData;
}

function getTitle(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);


    if (poiType === 'favourites') {
        if(poiResult.id === 'home') return 'Zuhause'
        if(poiResult.id === 'work') return 'Arbeit'
        return poiResult.name
    } else if( multiPoi ) {
        // exception for predictions in multi
        if(poiType === 'traffic' && poiResult.typeSpecificAttributes.traffic && poiResult.typeSpecificAttributes.traffic.type && poiResult.typeSpecificAttributes.traffic.type.includes('Prognose')) return getCategoryLabelByResult(poiType, poiResult)
        
        return poiResult.name;
    } else if (poiType === 'traffic' || poiType === 'camera') {
        if(poiResult.subType && poiResult.subType === 'camera') {
            return poiResult.computed ? poiResult.computed.displayName : poiResult.header.names[0].value
        }
        if(poiResult.subType === 'prediction') {
            return getCategoryLabelByResult(poiType, poiResult)
        }
        return poiResult.categorization ? poiResult.categorization.type : poiResult.type;
    } else if (poiType === 'nearbyStations') {
        if(poiResult.departures) {
            return poiResult.departures[0].stop
        }
        return poiResult.name;
    } else if(poiType === 'travelRadar') {
        return poiResult.header ? poiResult.header.title : poiResult.title
    } else if (poiType === 'chargingStations') {
        return poiResult.data.operator.companyName
    } else if(poiType === 'oeamtcLocations') {
        return poiResult.computed.displayName
    } else {
        let name = false
        if(poiResult.computed && poiResult.computed.displayAddress.name && poiType !== 'passRoads' && poiType !== 'tollRoads') {
            name = poiResult.computed.displayAddress.name
        } else if(poiResult.data.header.names && poiResult.data.header.names.length > 0) {
            const nameItem = poiResult.data.header.names.find(item => item.type === 'NAME')
            if(nameItem) {
                name = nameItem.value
            }
        }
        
        return name ?? `${getCategoryLabelByResult(poiType, poiResult) ?? ''} ${poiResult.computed.displayAddress.city}`;
    }
}

function getCategoryLabelByResult(poiType, poiResult) {
    const category = getCategory(poiType, poiResult);
    return getCategoryLabelByValue(poiType, category)
}

function getDetailNameByType(poiType) {
    const configType = checkIsSubApi(poiType) ? getParentApi(poiType) : poiType
    const poiTypeConfig = getPoiConfig(configType);

    return poiTypeConfig.detailName || false
}

export function getCategoryLabelByValue(poiType, category) {
    const configType = checkIsSubApi(poiType) ? getParentApi(poiType) : poiType
    const poiTypeConfig = getPoiConfig(configType);
    if(!poiTypeConfig.categories) return false; 
    let categoryConfig
    for (const c of poiTypeConfig.categories) {
        if(c.value === category) {
            categoryConfig = c;
        }
        if(c.children) {
            for (const cChild of c.children) {
                if(cChild.value === category) {
                    categoryConfig = cChild;
                    break;
                }
            }
        }
    }
    if (!categoryConfig) return false;
    return categoryConfig.labelAlt
        ? categoryConfig.labelAlt
        : categoryConfig.label;
}

// needed for gas station favourites
export function getCategoryValueByLabel(poiType, label) {
    const configType = checkIsSubApi(poiType) ? getParentApi(poiType) : poiType
    const poiTypeConfig = getPoiConfig(configType);
    if(!poiTypeConfig.categories) return false; 
    let categoryConfig
    for (const c of poiTypeConfig.categories) {
        if(c.label === label || c.labelAlt === label) {
            categoryConfig = c;
        }
        if(c.children) {
            for (const cChild of c.children) {
                if(cChild.label === label || cChild.labelAlt === label) {
                    categoryConfig = cChild;
                    break;
                }
            }
        }
    }
    if (!categoryConfig) return false;
    return categoryConfig.value
}

function getAddress(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if( multiPoi ) {
        if(isShorttermParking(poiType, poiResult)) return false;

        if(poiType === 'traffic' && poiResult.type === 'TRAFFIC') {
            return poiResult.typeSpecificAttributes.traffic.description
        }

        if(poiType === 'favourites') {
            if(poiResult.id === 'home' || poiResult.id === 'work')  return poiResult.name
            if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.passroad) return `${poiResult.typeSpecificAttributes.passroad.roadName ? `${poiResult.typeSpecificAttributes.passroad.roadName}: ` : ''}${poiResult.typeSpecificAttributes.passroad.fromTo}`
        }

        if(poiResult.street || poiResult.houseNumber || poiResult.postalCode || poiResult.city) {
            return `${poiResult.street ?? ''}${poiResult.houseNumber ? ` ${poiResult.houseNumber}` : ''}${poiResult.street || poiResult.houseNumber ? ', ' : ''}
                ${poiResult.postalCode ? `${poiResult.postalCode} ` : ''}
                ${poiResult.city || ''}`;
        } 
    } else if (poiType === 'traffic' || poiType === 'camera') {
        if(poiResult.subType && poiResult.subType === 'camera') {
            return poiResult.computed && poiResult.computed.displayAddress ? poiResult.computed.displayAddress.city : poiResult.addresses[0].city
        }
        return poiResult.description ? poiResult.description.shortDescription : false;
    } else if (poiType === 'nearbyStations') {
        return false;
    } else if(poiType === 'favourites') {
        return poiResult.title
    } else if(poiType === 'greenzone') {
        const displayAddress = poiResult.computed.displayAddress;
        return displayAddress.country
    } else if(poiType === 'passRoads' || poiType === 'roadHouses' || poiType === 'tollRoads' || poiType === 'tunnels') {
        if(poiResult.data.roadConnection.roadName && poiResult.data.roadConnection.fromTo) return `${poiResult.data.roadConnection.roadName}: ${poiResult.data.roadConnection.fromTo}`
        if(poiResult.data.roadConnection.roadName) return poiResult.data.roadConnection.roadName
        if(poiResult.data.roadConnection.fromTo) return poiResult.data.roadConnection.fromTo
    } else if(isShorttermParking(poiType, poiResult)) {
        return false
    } else if(poiType === 'travelRadar') {
        return poiResult.header ? poiResult.header.region : false
    } else {
        if(poiResult.computed) {
            const displayAddress = poiResult.computed.displayAddress;
            return `${displayAddress.street ?? ''}${displayAddress.houseNumber ? ` ${displayAddress.houseNumber}` : ''}${displayAddress.street || displayAddress.houseNumber ? ', ' : ''}
                ${displayAddress.postalCode ? displayAddress.postalCode : ''} 
                ${displayAddress.city}`;
        }
        return false
    }
}

function getZip(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if( multiPoi ) {
        return poiResult.postalCode;
    } else if (poiType === 'traffic') {
        return poiResult.description ? poiResult.description.shortDescription : false;
    } else if (poiType === 'nearbyStations' || poiType === 'travelRadar') {
        return false;
    } else if(poiType === 'favourites') {
        return false
    } else {
        return poiResult.computed.displayAddress.postalCode;
    }
}

function getCity(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if( multiPoi ) {
        return poiResult.city;
    } else if (poiType === 'traffic') {
        return poiResult.description ? poiResult.description.shortDescription : false;
    } else if (poiType === 'nearbyStations') {
        return false;
    } else if(poiType === 'favourites') {
        return false
    } else if(poiType === 'travelRadar') {
        return poiResult.data ? poiResult.data.city : false;
    } else {
        return poiResult.computed.displayAddress.city;
    }
}

function getImageForPoiCategory(poiType, poiResult, multiPoi = null) {
    let category = getCategory(poiType, poiResult, multiPoi);
    return getImageForCategory(poiType, category, 'image', poiResult);
}

export function getCategory(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if( multiPoi ) {
        if(poiType === 'partners') {
            return poiResult.type // todo: only for testing to show the main types
        } else if(poiType === 'carParks') {
            if(isShorttermParking(poiType, poiResult)) {
                const specificCategory = poiResult.typeSpecificAttributes.shorttermparking.categories[0]
                return specificCategory.replace('SHORTTERMPARKING_', '')
            }
            return poiResult.typeSpecificAttributes.parking.categories[0];
        } else if(poiType === 'traffic') {
            if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.traffic && poiResult.typeSpecificAttributes.traffic.type) {
                return poiResult.typeSpecificAttributes.traffic.type
            } else {
                return poiResult.type
            }
        } else if(poiType === 'favourites') {
            if(poiResult.id === 'home' || poiResult.id === 'work') {
                return poiResult.id
            } else if(poiResult.type === 'ADDRESS') {
                return 'ADDRESS' 
            } else if(poiResult.type === 'FUEL') {
                // TODO: remove as soon as fuel categories work
                return 'gasStations'
            } else if(poiResult.type === 'CAMPING') {
                return 'CAMPING'
            } else if(poiResult.type === 'EFUEL') {
                if(poiResult.typeSpecificAttributes.efuel && poiResult.typeSpecificAttributes.efuel.oeamtcEPowerStation) return 'oeamtc-e-power'
                return 'default'
            } else if(poiResult.type === 'PARKING' || poiResult.type === 'OEAMTC' || poiResult.type === 'SIGHT') {
                const key = poiResult.type.toLowerCase()
                if(poiResult.typeSpecificAttributes[key] && poiResult.typeSpecificAttributes[key].categories) {
                    return poiResult.typeSpecificAttributes[key].categories[0]
                }
                if(poiResult.typeSpecificAttributes[key] && poiResult.typeSpecificAttributes[key].subType) {
                    return poiResult.typeSpecificAttributes[key].subType
                }
            } else if(poiResult.type === null && poiResult.subType === null && poiResult.poicat === 'PARTNER') {
                return 'PARTNER'
            }
            // TODO: implement proper subtypes as soon as returned by the api
            const typeConfig = getFavouriteTypeConfig(poiResult.type)
            return typeConfig.value || 'ADDRESS'
        }
        return false;
    } else if (poiType === 'traffic' || poiType === 'camera' || poiType === 'prediction') {
        if(poiResult.subType && poiResult.subType === 'camera') return 'CAMERA';
        return poiResult.categorization ? poiResult.categorization.type : poiResult.type;
    } else if (poiType === 'favourites') {
        if(poiResult.id === 'home' || poiResult.id === 'work') {
            return poiResult.id
        } else if(poiResult.id.includes('|')) {
            return poiResult.id.split('|')[1]
        }
        let category
        if(poiResult.type) {
            category = poiResult.type
        } else if(poiResult.poicat) {
            category = poiResult.poicat.includes('POI_') ? poiResult.poicat.replace('POI_', '') : poiResult.poicat
        }
        if(poiResult.subType && poiResult.subType !== '') {
            category = poiResult.subType
        } else if(category) {
            const poiConfig = getFavouriteTypeConfig(category)
            category = poiConfig.value

            if(category === 'gasStations') {
                return '0'
                // TODO: get correct subcategory
                // category = getCategoryValueByLabel(category, poiResult.subType) 
            }
            if(category === 'camping') {
                return 'CAMPING'
            }
            if(category === 'chargingStations') {
                return 'default'
            }
        }
        return category || 'ADDRESS'
    } else if(isPartner(poiType)) {
         if(poiResult.data.header && poiResult.data.header.categories) {
            return poiResult.data.header.categories[0]
        } else {
            return poiType.toUpperCase()
        }
    } else if(poiType === 'partners' && poiResult.subType) { // todo: for POI detail, might need improvement
        return poiResult.subType.toUpperCase()
    } else if (poiType === 'nearbyStations') {
        return false;
    } else if (poiType === 'greenzone') {
        return false
    } else if (poiType === 'tollRoads') {
        return false
    } else if (poiType === 'tunnels') {
        return false;
    } else if (poiType === 'passRoads') {
        return false
    } else if (poiType === 'roadHouses') {
        return false;
    } else if(poiType === 'ACCOMMODATION' || poiType === 'ENTERTAINMENT') {
        return poiType
    } else if(poiType === 'travelRadar') {
        return poiResult.header ? poiResult.header.level : poiResult.level
    } else if(poiType === 'chargingStations') {
        if(poiResult.data.header.oeamtcEPowerStation) return 'oeamtc-e-power'
        return 'default'
    } else {
        if(poiType === 'camping' && isOeccPartner(poiType, poiResult)) {
            return 'OECC';
        }
        if(poiType === 'gasStations') {
            return poiResult.computed && poiResult.computed.operatorLogoId ? parseInt(poiResult.computed.operatorLogoId) : 0;
        }
        return poiResult.data.header.categories[0];
    }
}

export function isOeamtcPartner(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    
    if( multiPoi ) {
        return poiResult.partnerInformation && poiResult.partnerInformation.oeamtcPartner;
    } else if (poiType === 'traffic' || poiType === 'camera' || poiType === 'prediction' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        const ratings = poiResult.data.ratings;
        return ratings && ratings.oeamtcPartner && ratings.oeamtcPartner.isRatingType
    }
}

export function isOeccPartner(poiType, poiResult) {
    if (poiType !== 'camping') return false;
    const ratings = poiResult.data.ratings;
    return ratings && ratings.oeccPartner && ratings.oeccPartner.isRatingType
}

function getPartnerTag(poiType, poiResult, multiPoi = null) { // percentage tag for oeamtc partners
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if(!isOeamtcPartner(poiType, poiResult, multiPoi)) return false;
    let content = '';

    if(multiPoi) {
        if(poiResult.partnerInformation.comment) {
            content = poiResult.partnerInformation.comment;
        }
    } else {
        if(poiResult.data.ratings.oeamtcPartner.shortComment) {
            content = poiResult.data.ratings.oeamtcPartner.shortComment[0].content;
        }
    }

    // check if the content from backend looks like this "15%" or "€ 7", otherwise display "%"
    if(!content.match(/^\d*%$/) && !content.match(/^€ \d*$/)) {
        content = '%';
    } else {
        // place minus before % and € and space between number and sign
        if(content.match(/^\d*%$/)) {
            content = content.replace('%', ' %')
        } else if(content.match(/^€ \d*$/)) {
            content = content.replace('€', ' €')
        }
        content = `- ${content}`
    }
    return content;
}

function getOeccPartnerDescription(poiType, poiResult) {
    if(!isOeccPartner(poiType, poiResult)) return false;
    return getHtmlDescription(poiResult.data.ratings.oeccPartner.longComment);
}

function getPartnerDescription(poiType, poiResult) {
    if(!isOeamtcPartner(poiType, poiResult)) return false;
    return getHtmlDescription(poiResult.data.ratings.oeamtcPartner.longComment);
}

function getComments(poiType, poiResult) {
    if(poiResult.subType !== 'shorttermparking') return false;
    return poiResult.data.addresses[0].comment[0];
}

function getDistance(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if(multiPoi) {
        return poiResult.distance * 1000;
    } else if (poiType === 'nearbyStations') {
        if(poiResult.departures && poiResult.departures[0] && poiResult.departures[0].distance) {
            return poiResult.departures[0].distance
        }
        return poiResult.distance;
    } else if (poiType === 'traffic' || poiType === 'camera' || poiType === 'favourites') {
        // todo: real values
        return false;
    } else {
        let distance = poiResult.computed.distanceToUserCoordinates;
        if (distance) {
            return distance * 1000;
        }
        return false;
    }
}

function getRoadName(poiType, poiResult) {
    if (poiType === 'traffic' || poiType === 'camera') {
        if(poiResult.typeSpecificAttributes) {
            if(poiResult.typeSpecificAttributes.camera && poiResult.typeSpecificAttributes.camera.roadId) {
                return poiResult.typeSpecificAttributes.camera.roadId
            }
            if(poiResult.typeSpecificAttributes.traffic && poiResult.typeSpecificAttributes.traffic.roadId && poiResult.typeSpecificAttributes.traffic.roadId.length <= 8) {
                return poiResult.typeSpecificAttributes.traffic.roadId
            }
        } else if(poiResult.road && poiResult.road.routeId && poiResult.road.routeId.length <= 8) {
            return poiResult.road.routeId;
        } else if(poiResult.roadConnection) {
            return  poiResult.roadConnection.roadNumber
        } else if(poiResult.data && poiResult.data.roadConnection) {
            return  poiResult.data.roadConnection.roadNumber
        }
    } else if(poiType === 'passRoads' || poiType === 'roadHouses' || poiType === 'tollRoads' || poiType === 'tunnels') {
        return poiResult.data.roadConnection.roadNumber
    } else if(poiType === 'favourites') {
        if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.passroad && poiResult.typeSpecificAttributes.passroad.roadId) return poiResult.typeSpecificAttributes.passroad.roadId
        if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.camera && poiResult.typeSpecificAttributes.camera.roadId) return poiResult.typeSpecificAttributes.camera.roadId
    } else {
        return false;
    }
}


function getBigImages(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);

    if(multiPoi) {
        return false;
    } 
    
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        let pictures = poiResult.data.header.pictures;
        if (pictures?.length) {
            return pictures;
        }
        return false;
    }
}

function getVideoPicture(poiType, poiResult) {
    if((poiType === 'traffic' || poiType === 'camera') && poiResult.subType && poiResult.subType === 'camera') {
        return {
            url: poiResult.data.videoLink.pictureUrl,
            alt: poiResult.computed.displayName,
            type: poiResult.data.videoLink.pictureUrl.endsWith('.jpg') || poiResult.data.videoLink.pictureUrl.endsWith('.png') ? 'image' : 'video'
        }
    } else {
        return false
    }
}

function getAvailability(poiResult, poiType, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    
    let utilization;

    if(multiPoi) {
        if(poiType === 'carParks' || poiType === 'favourites') {
            if(poiResult.type === 'PARKING') {
                utilization = poiResult.typeSpecificAttributes.parking ? poiResult.typeSpecificAttributes.parking.utilization : false;
            }
        }
    } else {
        if (!poiResult.computed || !poiResult.computed.utilization) return false;
        utilization = poiResult.computed.utilization;
    }

    if(!utilization) return false;

    let color = '';
    let label = '';
    if (utilization.hasFreeParkingLots) {
        color = 'green';
        if (utilization.freeParkingLots && utilization.maxParkingLots) {
            const takenPercentage = (utilization.freeParkingLots / utilization.maxParkingLots) * 100;
            if(takenPercentage > 95) color = 'orange';
            label = `${utilization.freeParkingLots}/${utilization.maxParkingLots}`;
        } else {
            label = 'frei';
        }
    } else {
        color = 'red';
        label = 'belegt';
    }
    return {
        color,
        label,
    };
}

function getFeeAvailability(poiType, poiResult) {
    const openingInformation = poiResult.computed && poiResult.computed.openingInformation ? poiResult.computed.openingInformation : poiResult.openingInformation;
    if(!openingInformation) return false;
    

    if(isShorttermParking(poiType, poiResult)) {
        const category = getCategory(poiType, poiResult)
        if(category === 'NON_PUBLIC') {
            return {
                color: 'orange',
                label: `Immer gültig`,
            };
        }

        const multiPoi = isResultMultiPoi(poiResult)
        const priceList = getPriceList(poiType, poiResult)
        let hasPrices = priceList && priceList.sections.defaultItems.length
        if(multiPoi) {
            if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.shorttermparking && poiResult.typeSpecificAttributes.shorttermparking.prices) {
                const priceData = poiResult.typeSpecificAttributes.shorttermparking.prices
                if(priceData[0] && priceData[0].prices) {
                    priceData[0].prices.forEach(price => {
                        if(price.price && price.price > 0) hasPrices = true
                    })
                }
            }
        }

        if(!hasPrices) {
            return {
                color: 'green',
                label: `Gebührenfrei`,
            };
        }

        if(openingInformation.openAllDay) {
            return {
                color: 'orange',
                label: 'Immer gültig',
            };
        }

        if(openingInformation.minutesLeftOpen) {
            if(openingInformation.minutesLeftOpen < 120) {
                return {
                    color: 'red',
                    label: `Noch ${formatDuration(openingInformation.minutesLeftOpen * 60)} gebührenpflichtig`,
                };
            } else {
                return {
                    color: 'red',
                    label: `Jetzt gebührenpflichtig`,
                };
            }
        }
    
        if(openingInformation.minutesLeftClosed) {
            if(openingInformation.minutesLeftClosed < 120) {
                return {
                    color: 'orange',
                    label: `Noch ${formatDuration(openingInformation.minutesLeftClosed * 60)} gebührenfrei`,
                };
            } else {
                return {
                    color: 'green',
                    label: `Jetzt gebührenfrei`,
                };
            }
        }
    }
    return false
}

function getTodayMaxOpeningHours(poiType, poiResult, multiPoi = null) {
    if(multiPoi === null) multiPoi = isResultMultiPoi(poiResult);
    if(multiPoi) return false;
    
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        return formatTodayOpeningHours(poiResult.computed.maxOpeningHours);
    }
}

function getEmail(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    }
    
    if (!poiResult.data.operator) return false;
    return poiResult.data.operator.email;
}

function getPhone(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    }
    if (!poiResult.data.operator) return false;
    return poiResult.data.operator.phone;
}

function getWebsite(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        if (!poiResult.data.operator) return false;
        return poiResult.data.operator.url;
    }
}

export function getOpeningSections(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        let alternateTitle = false
        let openingSections = [];
        let openingComments = []
        if(isShorttermParking(poiType, poiResult)) alternateTitle = 'Gültigkeitszeitraum'
        if(!poiResult.data || !poiResult.data.openings) return false;
        poiResult.data.openings.forEach((opening) => {
            if (!Object.keys(opening.openingHours).length) return true;
            if(opening.season && opening.season.to && opening.season.from) {
                const currentDate = new Date()
                currentDate.setHours(0,0,0,0);
                const seasonFrom = new Date(currentDate.getFullYear(), parseInt(opening.season.from.split('.')[1]) - 1, parseInt(opening.season.from.split('.')[0]), 0, 0, 0, 0)
                const seasonTo = new Date(currentDate.getFullYear(), parseInt(opening.season.to.split('.')[1]) - 1, parseInt(opening.season.to.split('.')[0]), 0, 0, 0, 0)

                if(!(currentDate <= seasonTo && currentDate >= seasonFrom)) return true
            }
            openingSections.push({
                label: opening.title,
                openingHours: formatOpeningHours(opening.openingHours),
            });
            if(opening.comment?.length && getHtmlDescription(opening.comment)) {
                openingComments = [...openingComments, ...getHtmlDescription(opening.comment)]
            }
        });

        return {
            title: alternateTitle,
            sections: openingSections,
            comments: openingComments?.length ? openingComments : false
        };
    }
}

function getTexts(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        if (!poiResult.data.operator) return false;
        return poiResult.data.operator.texts;
    }
}

function getAdditionalInformation(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'gasStations' || poiType === 'chargingStations') {
        if (!poiResult.data.operator || !poiResult.data.operator.comment || poiResult.data.operator.comment.length === 0 || !poiResult.data.operator.comment[0].content) return false;
        return {
            title: 'Weitere Informationen',
            text: getHtmlDescription(poiResult.data.operator.comment)
        };
    } else if(poiType === 'passRoads' || poiType === 'tollRoads') {
        if(!poiResult.data.openings || !poiResult.data.openings[0].comment) return false
        return {
            title: 'Anmerkungen',
            text: getHtmlDescription(poiResult.data.openings[0].comment)
        }
    } else if(poiType === 'travelRadar') {
        if(!poiResult.data || !poiResult.data.description) return false
        let description = poiResult.data.description.replace('<i></i>', ' ') // replace empty tags
        if(description.includes('----------------------------------')) description = description.replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, '') // get rid of the newlines
        const textsSplit = description.split('----------------------------------')
        let texts = textsSplit.map((t) => {
            const textSplitForTitle = t.includes('</i>') ? t.split('</i>') : false
            const titleContent = textSplitForTitle ? textSplitForTitle[0].replace('<i>', '') : false
            const textContent = textSplitForTitle ? textSplitForTitle.slice(1).join('</i>').trim() : false
            return {
                title: titleContent,
                content: textContent
            }
        })
        texts = texts.filter(t => t.title || t.content)
        return {
            title: texts[0].title ? texts[0].title : 'Update',
            text: [texts[0]],
            expandableText: texts.length > 1 ? texts.slice(1) : false, // only show the first 5 items,
            showMoreText: 'Mehr Updates anzeigen',
            hideMoreText: 'Weniger Updates anzeigen',
        }
    }
}

function getLongDescription(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic') {
        return poiResult.description ?  poiResult.description.longDescription : false;
        
    } else {
        return false;
    }
}

function getLastUpdated(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic') {
        let lastUpdatedDate
        if(poiResult.computed) {
            lastUpdatedDate = poiResult.computed.lastUpdated ? new Date(poiResult.computed.lastUpdated) : false;
        } else if(poiResult.header) {
            lastUpdatedDate = poiResult.header.lastUpdated ? new Date(poiResult.header.lastUpdated) : false;
        }
        return lastUpdatedDate ? `${formatDate(lastUpdatedDate)}, ${formatTime(lastUpdatedDate)}` : false;
    } else if(poiType === 'travelRadar') {
        const lastUpdated = poiResult.header ? poiResult.header.lastUpdated : poiResult.lastUpdated
        const lastUpdatedDate = lastUpdated ? new Date(lastUpdated) : false;
        return lastUpdatedDate ? `${formatDate(lastUpdatedDate)}, ${formatTime(lastUpdatedDate)}` : false;
    } else {
        return false;
    }
}

function getValidity(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic') {
        if(poiResult.validity) {
            const startDate = new Date(poiResult.validity.startTime);
            const stopDate = new Date(poiResult.validity.stopTime);
            return {
                start: `${formatDate(startDate)}, ${formatTime(startDate)}`,
                end: `${formatDate(stopDate)}, ${formatTime(stopDate)}`,
            };
        }
        return false
    } else if(poiType === 'travelRadar') {
        const startDate = poiResult.data && poiResult.data.timeStart ? new Date(poiResult.data.timeStart) : false;
        const stopDate = poiResult.data && poiResult.data.timeEnd ? new Date(poiResult.data.timeEnd) : false;
        return {
            start: startDate ? `${formatDate(startDate)}, ${formatTime(startDate)}` : '-',
            end: stopDate ? `${formatDate(stopDate)}, ${formatTime(stopDate)}` : 'noch aktiv',
        }
    } else {
        return false;
    }
}

function getSource(poiType, poiResult, multiPoi = false) {
    if(multiPoi) return false;
    if (poiType === 'traffic') {
        return poiResult.header ? poiResult.header.sourceProvider : false;
    } else if(poiType === 'travelRadar') {
        return poiResult.data && poiResult.data.source ? poiResult.data.source.name : false
    } else {
        return false;
    }
}

function getNearbyPOIs(poiType, poiResult) {
    if (poiType === 'traffic') {
        if (poiResult.computed && poiResult.computed.cameras) {
            const cameras = [];
            poiResult.computed.cameras.forEach((data) => {
                const fullData = {
                    ...data,
                    poiType: 'traffic',
                    subType: 'camera',
                }
                cameras.push(fullData);
            });
            return cameras;
        }
        return false;
    } else {
        return false;
    }
}

export function getCoordinatesObject(poiType, poiResult) {
    let coordinates;
    const multiPoi = isResultMultiPoi(poiResult);

    if(multiPoi) {
        coordinates = {
            latitude: poiResult.latitude ?? poiResult.lat,
            longitude: poiResult.longitude ?? poiResult.lon
        }
    } else if (poiType === 'traffic' || poiType === 'camera' || poiType === 'prediction') {
        if(poiResult.data && poiResult.data.geoData) {
            coordinates = poiResult.data.geoData.coordinates[0]
        } else {
            coordinates = poiResult.location ? poiResult.location.points[0] : {
                latitude: poiResult.latitude,
                longitude: poiResult.longitude
            }
        }
    } else if (poiType === 'nearbyStations') {
        if(poiResult.departures && poiResult.departures[0].coordinate) {
            coordinates = poiResult.departures[0].coordinate
        } else if(poiResult.inputParameters) {
            coordinates = poiResult.inputParameters.coordinate;
        } else {
            coordinates = poiResult.coordinate;
        }
    } else if (poiType === 'favourites') {
        coordinates = {
            latitude: poiResult.lat,
            longitude: poiResult.lon
        }
    } else if(poiType === 'travelRadar') {
        coordinates = poiResult.header ? poiResult.header.coordinate : poiResult.coordinate
    } else if (poiResult.computed && poiResult.computed.centerPoint) {
        coordinates = {
            latitude: poiResult.computed.centerPoint.latitude,
            longitude: poiResult.computed.centerPoint.longitude
        }
    } else if (poiResult.data.geoData && poiResult.data.geoData.coordinates) {
        coordinates = poiResult.data.geoData.coordinates[0];
    } else if(poiResult.computed && poiResult.computed.displayAddress && poiResult.computed.displayAddress.latitude) {
        coordinates = {
            latitude: poiResult.computed.displayAddress.latitude,
            longitude: poiResult.computed.displayAddress.longitude
        }
    }
    return coordinates;
}

export function poiInMapBounds(poiType, poiResult, { northWest, southEast }) {
    const { latitude, longitude } = getCoordinatesObject(poiType, poiResult);
    return (
        longitude > northWest.lng &&
        longitude < southEast.lng &&
        latitude > southEast.lat &&
        latitude < northWest.lat
    );
}

function getPaymentOptions(poiType, poiResult) {
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites') {
        return false;
    } else {
        const formattedPaymentOptions = [];
        const facilityData = poiResult.data ? poiResult.data.facilities : false;
        const mobileParkingData = poiResult.data ? poiResult.data.mobileParkings : false
        let hasComments = false
        if (facilityData && facilityData.paymentOptions) {
            const paymentOptions = facilityData.paymentOptions;
            paymentOptions.forEach((option) => {
                if (option.isAvailable) {
                    const comments = getHtmlDescription(option.comment)
                    if(comments) hasComments = true
                    formattedPaymentOptions.push({
                        title: getPaymentOptionLabel(option.type),
                        type: option.type,
                        comments: comments
                    });
                }
            });
        }
        if(mobileParkingData && mobileParkingData.length) {
            let mobileParkingDescription = ''
            const mobileParkingItems = []
            mobileParkingData.forEach(option => {
                if (option.isAvailable) {
                    mobileParkingDescription += `${getPaymentOptionLabel(option.type)}<br>`
                }
                if(option.isCoopPartner) {
                    mobileParkingItems.push({
                        type: option.type,
                        urls: option.url ? option.url.split(';') : false
                    })
                }
           }) 
           if(mobileParkingDescription !== '') {
                hasComments = true
                formattedPaymentOptions.unshift({
                    title: 'Mobile',
                    type: 'MOBILE',
                    comments: [{
                        content: mobileParkingDescription,
                    }],
                    values: mobileParkingItems
                });
           }
        }
        if(formattedPaymentOptions.length <= 0) return false
        return {
            hasComments,
            options: formattedPaymentOptions
        };
    }
}

function getFreeMinutes(poiType, poiResult) {
    if (poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else {
        const prices = poiResult.computed.currentPrices;
        if (!prices || !prices.prices) return false;
        const priceArray = prices.prices;
        const freeMinutesInfo = priceArray.filter(
            (price) => price.freeMinutes !== undefined,
        );
        const formattedFreeMinutes = [];
        if (freeMinutesInfo && freeMinutesInfo.length > 0) {
            freeMinutesInfo.forEach((info) => {
                if(info.freeMinutes > 0) {
                    formattedFreeMinutes.push(
                        `${info.freeMinutes} Minuten kostenlos parken (${getVehicleLabel(info.vehicle.type,)})`,
                    );
                }
            });
        }
        return formattedFreeMinutes;
    }
}

function getMaximumHeight(poiType, poiResult) {
    if(poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false
    } else {
        const facilityData = poiResult.data.facilities;
        if (!facilityData || !facilityData.parking) return false;
        const parkingInfo = facilityData.parking;
        for(let i = 0; i < parkingInfo.length; i++) {
            const info = parkingInfo[i]
            if(info.vehicle && info.vehicle.maximumHeight) {
                return formatHeight(info.vehicle.maximumHeight)
            }
        }
        return false
    }
}

function getAccsessibility(poiType, poiResult) {
    if(poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false
    } else {
        const accessibleData = poiResult.data.accessible;
        if (!accessibleData) return false;
        const formattedAccssessibilty = {
            isAccessible: false,
            accessibleComments: false,
            handicappedParking: false,
            handicappedParkingComments: false
        }
        if(accessibleData.accessible && accessibleData.accessible.isAccessible) formattedAccssessibilty.isAccessible = true
        if(accessibleData?.accessible?.comment?.length) formattedAccssessibilty.accessibleComments = getHtmlDescription(accessibleData.accessible.comment)
        if(accessibleData.handicappedParking && accessibleData.handicappedParking.isAccessible) formattedAccssessibilty.handicappedParking = true
        if(accessibleData.handicappedParkingLots && accessibleData.handicappedParkingLots > 0) formattedAccssessibilty.handicappedParkingLots = accessibleData.handicappedParkingLots
        if(accessibleData?.handicappedParking?.comment?.length) formattedAccssessibilty.handicappedParkingComments = getHtmlDescription(accessibleData.handicappedParking.comment)
        return formattedAccssessibilty
    }
}

function getParkingSpaces(poiType, poiResult) {
    const facilityData = poiResult.data.facilities;
    if (!facilityData || !facilityData.parking) return false;
    const formattedParkingSpaces = {
        default: [], // all exept electric_car
        electricCar: false
    }
    const parkingInfo = facilityData.parking;
    parkingInfo.forEach(info => {
        if(info.availability || poiType !== 'roadHouses') {
            const data = {
                amount: info.numberOfParkingLots ?? false,
                label: getVehicleLabel(info.vehicle.type)
            }
            if(poiType === 'roadHouses' || poiType === 'ACCOMMODATION' || poiType === 'GASTRONOMY') {
                data.label += '-Stellplätze vorhanden'
            }
            if(info.vehicle.type === 'ELECTRIC_CAR') {
                formattedParkingSpaces.electricCar = data
            } else {
                formattedParkingSpaces.default.push(data)
            }
        }
    })
    return formattedParkingSpaces
}

function getStationId(poiResult) {
    if(poiResult.departures) return poiResult.departures[0].externalId
    return poiResult.externalId
}

function getLines(poiResult) {
    if(poiResult.departures) return poiResult.departures.map(departure => departure.line)
    return poiResult.lines
}

function getServices(poiType, poiResult) {
    if(poiType === 'nearbyStations' || poiType === 'traffic' || poiType === 'camera' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false;
    } else if(poiType !== 'carParks' && poiType !== 'roadHouses') {
        const facilityData = poiResult.data.facilities;
        if (!facilityData) return false;
        const services = facilityData.services
        if(!services || services.length <= 0) return false
        const formattedServices = []
        services.forEach(service => {
            formattedServices.push(getServiceLabel(service))
        })
        return formattedServices
    } else {
        const facilityData = poiResult.data.facilities;
        if (!facilityData) return false;
        const comfortData = facilityData.comfort ?? false
        const safetyData = facilityData.safety ?? false
        const communicationData = facilityData.communication ?? false
        const childData = facilityData.child ?? false
        const formattedServices = []
        if(comfortData) {
            for(let key in comfortData) {
                const isAvailable = comfortData[key]
                if(isAvailable) formattedServices.push(getServiceLabel(key))
            }
        }
        if(safetyData) {
            for(let key in safetyData) {
                const isAvailable = safetyData[key].isAvailable
                if(isAvailable) formattedServices.push(getServiceLabel(key))
            }
        }
        if(communicationData) {
            for(let key in communicationData) {
                const isAvailable = communicationData[key]
                if(isAvailable) formattedServices.push(getServiceLabel(key))
            }
        }
        if(childData) {
            for(let key in childData) {
                const isAvailable = childData[key]
                if(isAvailable) formattedServices.push(getServiceLabel(key))
            }
        }
        return formattedServices
    }
}

function getOperationMode(poiType, poiResult) {
    if(poiType === 'traffic' || poiType === 'camera' || poiType === 'nearbyStations' || poiType === 'favourites' || poiType === 'travelRadar') {
        return false
    } else {
        const operationMode = poiResult.data.operationMode;
        return getOperationModeLabel(operationMode)
    }
}

// function getPublicTransportConnection(poiResult) {
//     const facilityData = poiResult.data.facilities;
//     if (!facilityData || !facilityData.publicTransportConnection) return false;
//     const connectionData = facilityData.publicTransportConnection
//     const formattedConnections = []
//     for(const key in connectionData) {
//         formattedConnections.push({
//             link: connectionData[key],
//             label: key,
//         })
//     }
//     return formattedConnections 
// }

function getCoordinates(poiResult) {
    if (poiResult.computed.displayAddress && poiResult.computed.displayAddress.latitude && poiResult.computed.displayAddress.longitude) {
        return `${poiResult.computed.displayAddress.latitude}, ${poiResult.computed.displayAddress.longitude}`;
    } else {
        return false;
    }
}

function getOpeningSeasons(poiResult) {
    const season = {
        current: 'Aktuell geschlossen',
        list: poiResult.data.openings && poiResult.data.openings.length > 1 ? poiResult.data.openings.map(opening => formatOpeningSeason(opening.season)) : false,
    }
    if(poiResult.computed.validSeason) {
        season.current = formatOpeningSeason(poiResult.computed.validSeason)
    }
    if(poiResult.data.openings && poiResult.data.openings[0] && poiResult.data.openings[0].season) {
        season.current = formatOpeningSeason(poiResult.data.openings[0].season)
    }
    return season
}

function convertNearbyStationsResults(poiResults) {
    poiResults = poiResults.stations;
    return poiResults;
}

export function getCurrentSorting(poiType = false, settings = false) {
    let sorting = store.getters.poiSorting
    const isPriceSorting = ['fuel'].includes(poiType)

    
    if((poiType && settings && isPriceSorting && !sorting) || (sorting && settings && sorting.includes('PRICE'))) { // only for API call
        // on initial load: check if loading single poi is of type price sorting
        const poisToLoad = settings.selectedPOIsForSorting || store.getters.loadingPOIs
        if (poisToLoad.length !== 1 || 
            !PRICE_SORTING_MODES.includes(poisToLoad[0]) || 
            Object.keys(store.getters.poiResults).length > 1 
        ) {
            sorting = false
        } else {
            let sortingPrefix = false
            let sortingType = false;
            const sortingDirection = sorting ? sorting.split('_')[1] : 'ASC'

            switch(poiType) {
                case 'fuel':
                    sortingPrefix = 'FUEL'
                    sortingType = settings.fuels.fuels.values[0]
                    break;
            }

            if(sortingPrefix && sortingType) {
                sorting = `${sortingPrefix}_PRICE_${sortingType}_${sortingDirection}`
            } else {
                sorting = false
            }
        }
    }
    return sorting ? sorting : store.getters.userCoordinates ? 'DISTANCE_ASC' : 'NAME_ASC';
}

export function getSubType(poiType, poiResult) {
    if(poiType === 'traffic') {
        // for cameras on traffic detail page
        if(poiResult.subType) return poiResult.subType
        // for traffic prediction in multi
        if(poiResult.typeSpecificAttributes && poiResult.typeSpecificAttributes.traffic && poiResult.typeSpecificAttributes.traffic.type && poiResult.typeSpecificAttributes.traffic.type.includes('Prognose')) return 'prediction'
    } 
    if(poiType === 'favourites' && poiResult.type === 'CAMPING') return false // since camping uses it as value and category value
    const multiPoi = isResultMultiPoi(poiResult);
    if(!multiPoi) {
        return poiResult.subType || false
    }
    const subType = poiResult.type
    const config = getPoiConfig(poiType)
    if(config.categories) {
        const catEl = config.categories.find(category => category.value === subType)
        if(catEl) return subType.toLowerCase() // TODO: check when multipoi categories are implemented, else always use lower case type (matches api type)
    }
    return false
}

export function getActiveSubApiTypes(poiConfig = false, params, poiType = false) {
    const config = poiConfig ? poiConfig : getPoiConfig(poiType)
    const type = config.value;
    let subApiTypes = [];
    switch(type) {
        case 'carParks': 
            if(params.category && params.category.values && params.category.values.includes('SHORTTERMPARKING') && store.getters.mapZoom && store.getters.mapZoom >= 11) {
                subApiTypes.push('shorttermparking');
            }
            break;
        case 'traffic': {
            const cameraOnly = params.type && params.type.values && params.type.values.length === 1 && params.type.values[0] === 'CAMERA'
            if(params.type && params.type.values && params.type.values.includes('CAMERA') && !params.time.prediction) {
                subApiTypes.push('camera');
            }
            if(params.time && !cameraOnly) {
                subApiTypes.push('prediction');
            }
            break;
        }
        case 'partners':
            if(params.category && params.category.values && params.category.values.length > 0) {
                params.category.values.forEach(categoryValue => {
                    const configCategory = config.categories.find(cat => cat.value === categoryValue)
                    subApiTypes.push(configCategory.apiType)
                })
            }
            break;
    }  
    return subApiTypes.length ? subApiTypes : false;
}

export function getSubApiTypes(poiType) {
    const poiConfig = getPoiConfig(poiType)
    if(poiConfig.categories) {
        let subApiTypes = []
        poiConfig.categories.forEach(category => {
            if(category.apiType) subApiTypes.push(category.apiType)
        })  
        return subApiTypes.length ? subApiTypes : false;
    }
    return false
}

export function getSubTypeCategory(poiType) {
    if(poiType === 'prediction') return { apiType: 'prediction' }
    const parentType = getParentApi(poiType)
    if(parentType) {
        const config = getPoiConfig(parentType)
        if(config.categories) {
            return config.categories.find(category => category.value === poiType || category.apiType === poiType)
        }
    }
    return false
}

export function getParentApi(poiType) {
    if(poiType === 'prediction') return 'traffic'
    for(const poiElement of poiElements) {
        const subApiTypes = getSubApiTypes(poiElement.value)
        if(subApiTypes && subApiTypes.includes(poiType) && poiElement.value !== 'favourites') { // todo: check later when favourites multi poi is implemented
            return poiElement.value
        }
    }
    return false
}

export function getPoiTypeByApi(apiType) {
    for(const poiElement of poiElements) {
        if(poiElement.apiType === apiType) {
            return poiElement.value
        }
        if(poiElement.categories) {
            for(const category of poiElement.categories) {
                if(category.apiType && category.apiType === apiType) {
                    return category.value
                }
            }
        }
    }
    return false
}

export function checkIsSubApi(poiType) {
    if(poiType === 'prediction') return true
    if(getParentApi(poiType)) return true
}

export function getPoiBySlug(slug) {
    for(const poiElement of poiElements) {
        if(poiElement.slug === slug) {
            return poiElement
        }
    }
    return false
}

export function isFavourite(poiId) {
    if(!store.getters.user) return false

    let foundFavourites = false
    foundFavourites = store.getters.allFavourites.filter(favourite => (poiId === favourite.objectId || poiId === favourite.id))
    if(foundFavourites && foundFavourites.length > 0) {
        return foundFavourites[0]
    } else if(poiId === 'home') {
        return store.getters.favouriteHome ?? false
    } else if(poiId === 'work') {
        return store.getters.favouriteWork ?? false
    }
    return false
}

export function isPartner(poiType) {
    return poiType === 'accommodation' || poiType === 'entertainment' || poiType === 'shopping' || poiType === 'gastronomy' || poiType === 'mobility' || poiType === 'leisure' || poiType === 'sports'
}

export function getOperator(poiType, poiResult) {
    if((poiType === 'traffic' || poiType === 'camera') && poiResult.subType && poiResult.subType === 'camera') {
            console.log(poiResult.data.operator)
        return {
            name: poiResult.data.operator?.companyName || poiResult.data.operator?.comment[0].content?.replace('ContactCompany: ', ''),
            imageUrl: poiResult.data.header.logos ? poiResult.data.header.logos[0].url : false,
            spaceLeft: false,
        }
    } else if(isShorttermParking(poiType, poiResult)) {
        if(!poiResult.data.operator || !poiResult.data.operator.companyName) return false
        return {
            name: poiResult.data.operator.companyName,
            imageUrl: false,
            spaceLeft: true,
        }
    } else if(poiType === 'oeamtcLocations' && poiResult.data.operator.comment) {
        return {
            comments: getHtmlDescription(poiResult.data.operator.comment),
        }
    } else {
        return false
    }
}

export function getPdf(poiType, poiResult) {
    if(poiType === 'greenzone') {
        const link = poiResult.computed.displayAddress.comment[0].content
        if(link.indexOf('pdf') < 0) return false
        return {
            link: link,
            title: `Umweltzonen in ${poiResult.computed.displayAddress.country}`
        }
    } 
    return false
}

export function getAdditionalLink(poiType, poiResult) {
    if(poiType === 'greenzone') {
        const link = poiResult.computed.displayAddress.comment[0].content
        if(link.indexOf('pdf') < 0) {
            return {
                link: link,
                title: `Umweltzonen in ${poiResult.computed.displayAddress.country}`
            }
        }
    } 
    return false
}

export function getFavouriteType(poiType, poiResult) {
    const configType = checkIsSubApi(poiType) ? getParentApi(poiType) : poiType
    const config = getPoiConfig(configType)
    const hasSubApis = getSubApiTypes(configType).length > 0
    let type = false
    if(hasSubApis) {
        for(const category of config.categories) {
            let isChildCategory = false
            if(category.children) {
                for(const childCategory of category.children) {
                    if(childCategory.value === poiResult.category) {
                        isChildCategory = true
                        type = childCategory.favouriteType || false
                        break;
                    }
                }
            }
            if((category.value === poiResult.category || (isChildCategory && !type)) && category.favouriteType) {
                type = category.favouriteType
                break;
            }
        }
    }
    if(!type && config.favouriteType) {
        type = config.favouriteType
    }
    return type
}

export function getFavouriteTypeConfig(favouriteType, parentOnly = false) {
    for(const poiElement of poiElements) {
        if(poiElement.value === 'favourites') continue;

        let poiElementData = {...poiElement}

        if(store.getters.dynamicPoiElements[poiElement.value]) {
            poiElementData = {...poiElement, ...store.getters.dynamicPoiElements[poiElement.value]}
        }

        if(poiElement.favouriteType && poiElement.favouriteType === favouriteType) {
            return getPoiConfig(poiElement.value)
        } 
        const subCategories = poiElementData.categories
        if(subCategories && subCategories.length > 0) {
            for(const subCategory of subCategories) {
                if(subCategory.favouriteType && subCategory.favouriteType === favouriteType) {
                    if(parentOnly) {
                        return getPoiConfig(poiElement.value)
                    } else {
                        return subCategory
                    }
                }
            }
        }
    }
    return false
}

export function getRoadInformation(poiType, poiResult) {
    if(poiType === 'passRoads' || poiType === 'tollRoads') {
        const roadData = {}
        if(poiResult.data.altitude) {
            roadData.altitude = poiResult.data.altitude
        }
        if(poiResult.data.fall) {
            roadData.fall = poiResult.data.fall
        }
        const restrictionsCamper = poiResult.data.roadConnection?.restrictions?.find(restriction => restriction.vehicle.type === 'CAMPER')
        roadData.camperAllowed = restrictionsCamper ? restrictionsCamper.type === 'ALLOWED' : false
        return roadData
    } else if(poiType === 'tunnels') {
        const roadData = {}
        if(poiResult.data.roadConnection.toll) {
            roadData.toll = poiResult.data.roadConnection.toll.isAvailable
        }
        if(poiResult.data.trafficInfrastructure && poiResult.data.trafficInfrastructure.building) {
            if(poiResult.data.trafficInfrastructure.building.length) {
                roadData.tunnelLength = poiResult.data.trafficInfrastructure.building.length
            }
            if(poiResult.data.trafficInfrastructure.building.pipes) {
                roadData.pipes = poiResult.data.trafficInfrastructure.building.pipes
            }
        }
        return roadData
    } else {
        return false
    }
}

export function getRoadFromTo(poiType, poiResult) {
    if(poiType === 'passRoads' || poiType === 'tollRoads') {
        return poiResult.data.roadConnection.fromTo
    } else {
        return false
    }
}

export function getTrafficMessages(poiType, poiResult) {
    if(poiType === 'passRoads' || poiType === 'tunnels' || poiType === 'tollRoads') {
        return poiResult.computed.trafficBroadcasts
    } else {
        return false
    } 
}

export function getMaxDuration(poiType, poiResult) {
    if(isShorttermParking(poiType, poiResult)) {
        if(poiResult.data && poiResult.data.maxDuration) {
            return `max. ${formatDuration(poiResult.data.maxDuration * 60, true, false, 'long')}`
        } else {
            return false
        }
    } 
    return false
}

export function getIntroText(poiType, poiResult) {
    if(isShorttermParking(poiType, poiResult)) {
        if(poiResult.data.prices && poiResult.data.prices[0].prices) {
            const pricesArray = poiResult.data.prices[0].prices
            if(pricesArray[0].comment) {
                return getHtmlDescription(pricesArray[0].comment)
            }
        }
        return false
    } else if(poiType === 'sights') {
        if(poiResult.data.description && poiResult.data.description.descriptions && poiResult.data.description.descriptions.length) {
            return getHtmlDescription(poiResult.data.description.descriptions)
        }
    }
    return false
}

export function getConnectedPois(poiResult) {
    // TODO: add again after connected pois are available
    if(poiResult.data.header && poiResult.data.header.references) {
        const references = poiResult.data.header.references
        references.forEach(reference => {
            reference.apiType = reference.type.toLowerCase()
            reference.poiType = getPoiTypeByApi(reference.apiType)
            reference.label = getPoiTypeLabel(reference.poiType)
        })
        return references.length > 0 ? references : false
    }
    return false
}

export function getCompany(poiType, poiResult) {
    if(poiType === 'roadHouses') {
        if(poiResult.data && poiResult.data.operator && poiResult.data.operator.companyName) {
            const multiPoi = isResultMultiPoi(poiResult);
            const category = getCategory(poiType, poiResult, multiPoi)
            const symbolUrl = getImageForCategory(poiType, category, 'symbol');
            return {
                name: poiResult.data.operator.companyName ?? false,
                image: symbolUrl
            }
        }
    } else if(poiResult.isChild) {
        if(poiType === 'gasStations') {
            if(poiResult.data && poiResult.data.operator && poiResult.data.operator.companyName) {
                const formattedPoiType = poiType === 'ACCOMMODATION' || poiType === 'GASTRONOMY' ? poiType.toLowerCase() : poiType
                const multiPoi = isResultMultiPoi(poiResult);
                const category = getCategory(formattedPoiType, poiResult, multiPoi)
                const symbolUrl = getImageForCategory(formattedPoiType, category, 'symbol');
                return {
                    name: poiResult.data.operator.companyName ?? false,
                    image: symbolUrl
                }
            }
        }
    }
    return false
}

export function getCountry(poiType, poiResult) {
    if(poiType === 'travelRadar') {
        if(poiResult.data && poiResult.data.administrativeLevel) {
            return {
                code: poiResult.data.administrativeLevel.iso2,
                name: poiResult.data.administrativeLevel.shortName
            }
        } else if(poiResult.header) {
            return {
                code: poiResult.header.iso2,
            }
        }
        return false
    }
    return false
}

export function getUpdatedAgo(poiType, poiResult) {
    if(poiType === 'travelRadar') {
        const lastUpdated = poiResult.header ? poiResult.header.lastUpdated : poiResult.lastUpdated
        return formatTimeAgo(lastUpdated)
    }
    return false
}

export function getPlugTypes(poiType, poiResult) {
    if(poiType === 'chargingStations') {
        if(poiResult.data && poiResult.data.chargingPoints && poiResult.data.chargingPoints.length) {
            const config = getPoiConfig(poiType);
            const configPlugs = config.plugs
            let plugTypes = []
            poiResult.data.chargingPoints.forEach(point => {
                point.plugs.forEach(plug => {
                    const plugExists = plugTypes.find(plugType => (plugType.plug === plug && plugType.kW === point.capacityKw))
                    if(plugExists) {
                        plugExists.amount++
                    } else {
                        const configPlug = configPlugs.find(cPlug => cPlug.value === plug || (cPlug.subValues && cPlug.subValues.includes(plug)))
                        plugTypes.push({
                            name: configPlug.label,
                            plug: plug,
                            kW: point.capacityKw,
                            icon: configPlug.icon || 'IconChargingStation',
                            amount: 1,
                        })
                    }
                })
            })
            plugTypes = plugTypes.sort((a, b) => {
                if (a.kW === b.kW){
                    return a.name < b.name ? -1 : 1
                  } else {
                    return a.kW < b.kW ? -1 : 1
                  }
            })
            return plugTypes.length > 0 ? plugTypes : false
        }
    }
    return false
}

export function getDetailName(poiType, poiResult) {
    if(poiType === 'chargingStations') {
        const config = getPoiConfig(poiType);
        return {
            name: poiResult.computed.displayAddress.name,
            icon: poiResult.data.header.oeamtcEPowerStation ? 'IconOEAMTC' : config.icon
        }
    } else if(poiResult.isChild) {
        if(poiType === 'ACCOMMODATION' || poiType === 'GASTRONOMY') {
            const formattedPoiType = poiType.toLowerCase()
            const multiPoi = isResultMultiPoi(poiResult);
            const category = getCategory(formattedPoiType, poiResult, multiPoi)
            const symbolUrl = getImageForCategory(formattedPoiType, category, 'symbol');
            return {
                name: poiResult.data.header.names[0].value ?? false,
                image: symbolUrl
            }
        }
    }
    return false
}

export function getAuthentication(poiType, poiResult) {
    if(poiType === 'chargingStations') {
        const formattedAuthOptions = [];
        const facilityData = poiResult.data ? poiResult.data.facilities : false;
        if (facilityData && facilityData.authenticationOptions) {
            const authOptions = facilityData.authenticationOptions;
            authOptions.forEach((option) => {
                if (option.isAvailable) {
                    formattedAuthOptions.push({
                        title: getAuthOptionLabel(option.type),
                        type: option.type,
                    });
                }
            });
        }
        return formattedAuthOptions
    }
    return false
}